import {
  ExceededMaxLengthError,
  PDFArray,
  PDFCheckBox,
  PDFDocument,
  PDFField,
  PDFFont,
  PDFNumber,
  PDFTextField,
  setFontAndSize,
  StandardFonts,
} from 'pdf-lib';
import {Mappings, PdfFieldType} from '@/engine/types/renderer/pdf';
import {SupportedLocale} from '@/initializers/i18next';
import {logger} from '@/initializers/logger';
import {LocalizedString, Mapper} from '@/model/types';
import {ValueResolver, VisibilitySet} from '../../types';
import {addCustomMappings} from './custom-mappings';
import {createFieldParser, normalize, normalizeSignature} from './parser';
import {getPdfFieldType, normalizeIndexes, safeResolver} from './utils';

const TEXT_LENGTH_LIMITS = [10, 25, 40, 60];
const FONT_SIZE_BOX_SCALE = 0.8;
const FONT_SIZE_AUTO_FIELDS = new Map<string, number>([['I99_info', 11]]);

const templateCache = new Map<string, ArrayBuffer>();
const stampCache = new Map<string, string>();

const isTruthy = (value: string): boolean => ['true', 'yes', 'oui'].includes(value);

interface MappingObject {
  mappings: Mappings;
  replacements: Record<string, string>;
  signatures: string[];
  stamps: Record<string, string>;
}

export const loadStamp = async (stampName: string) => {
  if (!stampCache.has(stampName)) {
    const response = await fetch('/stamp/' + stampName + '.png');
    const buffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(buffer);
    const data = btoa(uint8Array.reduce((data, byte) => data + String.fromCharCode(byte), ''));

    stampCache.set(stampName, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return stampCache.get(stampName)!;
};

export const loadTemplate = async (filename: LocalizedString, locale: SupportedLocale) => {
  if (!templateCache.has(filename[locale])) {
    const response = await fetch('/pdf/' + filename[locale]);
    const data = await response.arrayBuffer();
    templateCache.set(filename[locale], data);
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return templateCache.get(filename[locale])!;
};

export const getMappings = async (
  templatePdf: ArrayBuffer | null,
  mappers: Mapper[],
  replacements: Record<string, string>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
  locale: SupportedLocale,
): Promise<MappingObject> => {
  const mappings = {} as Record<string, string | boolean>;
  const signatures = [] as string[];
  const stamps = {} as Record<string, string>;

  if (!templatePdf) return {mappings, replacements, signatures, stamps};

  const PDF = await PDFDocument.load(templatePdf);
  const fields = PDF.getForm().getFields();

  const parser = createFieldParser(mappers, safeResolver(valueResolver), locale);

  for (const field of fields) {
    const fieldName = replacements[field.getName()] ?? field.getName();
    const fieldType = getPdfFieldType(field, fieldName);

    switch (fieldType) {
      case PdfFieldType.Text:
        mappings[fieldName] = parser(normalize(fieldName), visibilitySet);
        break;
      case PdfFieldType.Checkbox:
        mappings[fieldName] = isTruthy(parser(normalize(fieldName), visibilitySet).toLowerCase());

        break;
      case PdfFieldType.Signature:
        {
          const signatureField = normalizeSignature(fieldName);
          if (isTruthy(parser(signatureField, visibilitySet))) signatures.push(signatureField);
        }
        break;
      case PdfFieldType.Stamp:
        stamps[fieldName] = await loadStamp(fieldName);
        break;
      case PdfFieldType.Empty:
        break;
      default:
        logger.warn(`Unknown mapping on field type: ${fieldType} for field: ${fieldName}`);
        break;
    }
  }

  const customMappings = addCustomMappings(mappings, safeResolver(valueResolver), visibilitySet, locale);

  // Disabling normalization for now to have all values even with blank rows.
  const normalizedMappings = normalizeIndexes(customMappings);

  return {
    mappings: normalizedMappings,
    replacements,
    signatures,
    stamps,
  };
};

const fillTextPermissively = (field: PDFTextField, fieldName: string, text: string) => {
  try {
    (field as PDFTextField).setText(text);
  } catch (error: unknown) {
    if ((error as ExceededMaxLengthError).message.includes('maxLength')) {
      for (const newMaxLength of TEXT_LENGTH_LIMITS) {
        try {
          logger.warn(
            `Exceeded max length for field: ${fieldName} with text: ${text}, trying with new max length: ${newMaxLength}`,
          );
          (field as PDFTextField).setMaxLength(newMaxLength);
          (field as PDFTextField).setText(text);
          return;
        } catch (_error: unknown) {
          // Do nothing, try with next max length
        }
      }
      throw error;
    } else {
      throw error;
    }
  }
};

const normalizeRect = (rect?: PDFArray): PDFArray | undefined => {
  if (!rect) return undefined;

  const [x1, y1, x2, y2] = rect.asArray().map(obj => (obj as PDFNumber).asNumber());

  // Ensure lower-left and upper-right ordering by finding min/max values
  const llx = Math.min(x1, x2); // Lower-left x (minimum x)
  const lly = Math.min(y1, y2); // Lower-left y (minimum y)
  const urx = Math.max(x1, x2); // Upper-right x (maximum x)
  const ury = Math.max(y1, y2); // Upper-right y (maximum y)

  const normalized = rect.clone();

  normalized.set(0, PDFNumber.of(llx));
  normalized.set(1, PDFNumber.of(lly));
  normalized.set(2, PDFNumber.of(urx));
  normalized.set(3, PDFNumber.of(ury));

  return normalized;
};

const fontSizeForFieldAndText = (field: PDFField, font: PDFFont, value: string) => {
  const rect = field.acroField.getWidgets()[0].Rect();
  const box = normalizeRect(rect)?.asRectangle();
  if (!box) return 0;

  const fitText = (size: number): number =>
    font.widthOfTextAtSize(value, size) > box.width * FONT_SIZE_BOX_SCALE ? fitText(size - 1) : size;

  const fontSize = fitText(font.sizeAtHeight(box.height * FONT_SIZE_BOX_SCALE));

  return Math.max(fontSize, 5);
};

export const fillTemplate = async (
  templatePdf: ArrayBuffer | null,
  mappers: Mapper[],
  replacements: Record<string, string>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
  locale: SupportedLocale,
  debugFieldName?: boolean,
): Promise<string> => {
  if (!templatePdf) return '';

  const PDF = await PDFDocument.load(templatePdf);
  const font = await PDF.embedFont(StandardFonts.Courier);

  const {mappings} = await getMappings(templatePdf, mappers, replacements, valueResolver, visibilitySet, locale);

  const fields = PDF.getForm().getFields();

  for (const field of fields) {
    const fieldName = replacements[field.getName()] ?? field.getName();
    const fieldType = getPdfFieldType(field, fieldName);

    switch (fieldType) {
      case PdfFieldType.Text:
        try {
          let text = mappings[fieldName] as string;

          if (debugFieldName) text = fieldName;

          const fontSize = FONT_SIZE_AUTO_FIELDS.get(fieldName) || fontSizeForFieldAndText(field, font, text);
          const defaultAppearance = (field as PDFTextField).acroField.getDefaultAppearance() ?? '';
          const newDefaultAppearance = defaultAppearance + '\n' + setFontAndSize(font.name, fontSize).toString();
          (field as PDFTextField).acroField.setDefaultAppearance(newDefaultAppearance);

          fillTextPermissively(field as PDFTextField, fieldName, text);
        } catch (error: unknown) {
          logger.warn(error);
        }

        break;
      case PdfFieldType.Checkbox:
        if (mappings[fieldName] as boolean) {
          (field as PDFCheckBox).check();
        } else {
          (field as PDFCheckBox).uncheck();
        }
        break;
      case PdfFieldType.Stamp:
        await drawStampToPDF(PDF, field);

        break;
      case PdfFieldType.Empty:
        break;
      case PdfFieldType.Signature:
        break;
      default:
        logger.warn(`Unknown fill data on field type: ${fieldType} for field: ${fieldName}`);
        break;
    }

    field.enableReadOnly();
  }

  PDF.getForm().updateFieldAppearances(font);

  try {
    PDF.getForm().flatten();
  } catch (error: unknown) {
    const fields = PDF.getForm().getFields();
    for (const f of fields) {
      try {
        for (const widget of f.acroField.getWidgets()) {
          widget.getNormalAppearance();
        }
      } catch {
        logger.error(`Flatten crash : might be caused by field: "${f.getName()}" (${f.constructor.name})`);
      }
    }
    throw error;
  }

  return PDF.saveAsBase64({dataUri: true});
};

async function drawStampToPDF(PDF: PDFDocument, field: PDFField) {
  const fieldName = field.getName();

  const stampBase64 = await loadStamp(fieldName);
  const imageBytes = Uint8Array.from(atob(stampBase64), c => c.charCodeAt(0));
  const image = await PDF.embedPng(imageBytes);
  const page = PDF.getPage(0);
  page.drawImage(image, {
    ...field.acroField.getWidgets()[0].getRectangle(),
  });
}
